import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {concatMap, forkJoin, map, Observable, of, take} from 'rxjs';
import {
  ContextData,
  ProductQuery,
  productQueryToProductParametersDto,
  quoteQueryFactory,
} from '../../../common/context-data';
import {
  selectContextData,
  selectHasRegistrationCancellationRecommendation,
} from '../../../common/context-data/context-data.selectors';
import {
  ProductComponentModel,
  productComponentModelFactory,
  ProductType,
  Quote,
} from '../../model';
import {
  Bundle,
  ClareApiResponse,
  Product,
} from '@sportngin/se-commerce-sdk/models/clare';
import {CommerceApiService} from './commerce-api.service';
import {QuoteApiService} from './quote-api.service';
import {normalizeCatalogProduct} from './catalog-api-adapter';

export interface CatalogApi {
  getQualifyingCatalogIds(query: ProductQuery): Observable<string[]>;
  // [2025-11-10] TODO DWB: Remove this comment once deploy is triggered by CI/CD
  getProductsByCatalogId(
    catalogId: string,
  ): Observable<ProductComponentModel<ProductType>>;
}

export abstract class CatalogApiBase
  extends CommerceApiService
  implements CatalogApi
{
  protected constructor(
    protected http: HttpClient,
    protected quoteService: QuoteApiService,
    protected store: Store,
  ) {
    super(http, store);
  }

  getQualifyingCatalogIds(query: ProductQuery): Observable<string[]> {
    const url = this.getUrl('v3/commerce/shopper/catalog/qualifyingIdsMap');
    const parameters = productQueryToProductParametersDto(query);
    return this.POST<Record<string, string[]>>(
      url,
      parameters,
      this.getOptions({withCredentials: true}),
    ).pipe(map(response => Object.keys(response)));
  }

  protected addQuote(
    items: Array<Product | Bundle>,
    catalogKey?: string,
  ): Observable<ProductComponentModel<ProductType>> {
    return forkJoin([
      this.requestQuote(items[0]),
      of(items),
      this.contextData(),
    ]).pipe(
      map(([quote, items, context]) => {
        return productComponentModelFactory(items, context!, quote, catalogKey);
      }),
    );
  }

  private requestQuote(item: Product | Bundle): Observable<Quote | undefined> {
    const quoteProvider = item.display_metadata?.quote_provider;
    if (!quoteProvider) {
      return of(undefined);
    }

    return forkJoin([
      this.contextData(),
      this.store.pipe(
        select(selectHasRegistrationCancellationRecommendation),
        take(1),
      ),
    ]).pipe(
      concatMap(([context, hasRec]) => {
        if (!context) {
          throw new Error('Context data is not available');
        }
        const query = quoteQueryFactory(context, quoteProvider, hasRec);

        return this.quoteService.getQuote(query);
      }),
    );
  }

  private contextData(): Observable<ContextData | undefined> {
    return this.store.select(selectContextData).pipe(take(1));
  }

  abstract getProductsByCatalogId(
    catalogId: string,
  ): Observable<ProductComponentModel<ProductType>>;
}

@Injectable({providedIn: 'root'})
export class CatalogApiService extends CatalogApiBase {
  controllerPath = '';

  constructor(
    protected http: HttpClient,
    protected quoteService: QuoteApiService,
    protected store: Store,
  ) {
    super(http, quoteService, store);
  }

  getProductsByCatalogId(
    catalogKey: string,
  ): Observable<ProductComponentModel<ProductType>> {
    return this.getProductsByCatalogFromProductsEndpoint(catalogKey);
  }

  /**
   * SDK Clare `Product[]` from `finance/catalogs/:id/products`.
   */
  private getProductsByCatalogFromProductsEndpoint(
    catalogKey: string,
  ): Observable<ProductComponentModel<ProductType>> {
    const url = this.getUrl(`finance/catalogs/${catalogKey}/products`);
    return this.GET<ClareApiResponse<Product[]>>(
      url,
      this.getOptions({withCredentials: true}),
    ).pipe(
      concatMap((resp: ClareApiResponse<Product[]>) => {
        const items = resp.result.map(normalizeCatalogProduct);
        return this.addQuote(items, catalogKey);
      }),
    );
  }
}
